import { SyntheticEvent, useEffect, useMemo, useState } from 'react'

import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    InputAdornment,
    LinearProgress,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material'

import axios from 'axios'
import moment from 'moment'
import 'moment/locale/id'

import Icon from '../../../components/icon'
import MsiDatagrid from '../../../components/custom/MsiDatagridAuto'
import { useToast } from '../../../components/ToastProvider'
import apiUrl from '../../../configs/constant'

import { getPeriode } from '../../../api/helper'

moment.locale('id')

interface PeriodeOption {
    id: string | number
    periode: string

    tgl_awal?: string
    tgl_akhir?: string

    tanggal_awal?: string
    tanggal_akhir?: string

    awal?: string
    akhir?: string

    kode_periode?: string

    [key: string]: unknown
}

interface GudangOption {
    id: string
    nama: string
}

type ReportRow = Record<string, unknown>

const noActions: any[] = []

/*
|--------------------------------------------------------------------------
| Minimal periode
|--------------------------------------------------------------------------
|
| Requirement:
| periode dimulai dari Juni ke atas.
|
| Di sini kita mulai dari:
| 1 Juni 2026
|
*/
const MIN_PERIODE = '2026-06-01'

const readToken = (): string => {
    try {
        const rawUser = localStorage.getItem('user')

        if (!rawUser) return ''

        return String(JSON.parse(rawUser)?.token ?? '')
    } catch {
        return ''
    }
}

const getErrorMessage = (
    error: unknown,
    fallback: string
): string => {
    if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message

        if (
            typeof message === 'string' &&
            message.trim()
        ) {
            return message.replace(/<br\s*\/?>/gi, '\n')
        }

        return error.message || fallback
    }

    if (error instanceof Error) {
        return error.message
    }

    return fallback
}

const normalizePeriodeResponse = (
    response: any
): PeriodeOption[] => {
    if (Array.isArray(response)) {
        return response
    }

    if (Array.isArray(response?.data)) {
        return response.data
    }

    if (Array.isArray(response?.data?.data)) {
        return response.data.data
    }

    return []
}

/*
|--------------------------------------------------------------------------
| Mengambil tanggal awal periode
|--------------------------------------------------------------------------
|
| Dibuat fleksibel karena response helper getPeriode bisa memiliki
| nama field yang berbeda.
|
*/
const getPeriodeDate = (
    periode: PeriodeOption
): moment.Moment | null => {
    const possibleDate =
        periode.tgl_awal ??
        periode.tanggal_awal ??
        periode.awal

    if (possibleDate) {
        const parsed = moment(
            String(possibleDate),
            [
                'YYYY-MM-DD',
                'YYYY-MM-DD HH:mm:ss'
            ],
            true
        )

        if (parsed.isValid()) {
            return parsed
        }
    }

    /*
     * Fallback membaca label periode.
     *
     * Contoh:
     * Juni 2026
     * June 2026
     * 06-2026
     * 2026-06
     */
    const label = String(
        periode.periode ??
        periode.kode_periode ??
        ''
    ).trim()

    if (!label) {
        return null
    }

    const formats = [
        'MMMM YYYY',
        'MMM YYYY',
        'MM-YYYY',
        'MM/YYYY',
        'YYYY-MM',
        'YYYY/MM'
    ]

    const parsed = moment(label, formats, true)

    return parsed.isValid()
        ? parsed
        : null
}

const MetricCard = ({
    icon,
    label,
    value,
    helper
}: {
    icon: string
    label: string
    value: string | number
    helper: string
}) => (
    <Paper
        variant='outlined'
        sx={{
            height: '100%',
            p: 2.25,
            borderRadius: 3,
            borderColor: 'divider',
            backgroundColor: 'background.paper'
        }}
    >
        <Stack
            direction='row'
            spacing={1.75}
            alignItems='center'
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: 2.25,
                    display: 'grid',
                    placeItems: 'center',
                    color: 'primary.main',
                    backgroundColor: 'action.hover'
                }}
            >
                <Icon
                    icon={icon}
                    fontSize={24}
                />
            </Box>

            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant='caption'
                    color='text.secondary'
                >
                    {label}
                </Typography>

                <Typography
                    variant='h6'
                    sx={{
                        lineHeight: 1.25,
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {value}
                </Typography>

                <Typography
                    variant='caption'
                    color='text.secondary'
                >
                    {helper}
                </Typography>
            </Box>
        </Stack>
    </Paper>
)

const EmptyState = ({
    type,
    hasSearched
}: {
    type: 'periode' | 'gudang'
    hasSearched: boolean
}) => {
    const isPeriode = type === 'periode'

    return (
        <Box
            sx={{
                minHeight: 300,
                px: 3,
                py: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
            }}
        >
            <Box sx={{ maxWidth: 450 }}>
                <Box
                    sx={{
                        width: 72,
                        height: 72,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        color: 'primary.main',
                        backgroundColor: 'action.hover'
                    }}
                >
                    <Icon
                        icon={
                            hasSearched
                                ? 'mdi:database-off-outline'
                                : isPeriode
                                    ? 'mdi:chart-box-outline'
                                    : 'mdi:warehouse'
                        }
                        fontSize={34}
                    />
                </Box>

                <Typography
                    variant='h6'
                    sx={{
                        mb: 0.75,
                        fontWeight: 700
                    }}
                >
                    {hasSearched
                        ? 'Data tidak ditemukan'
                        : isPeriode
                            ? 'Pilih periode valuasi'
                            : 'Pilih gudang dan tanggal'}
                </Typography>

                <Typography
                    variant='body2'
                    color='text.secondary'
                >
                    {hasSearched
                        ? 'Stored procedure tidak mengembalikan data berdasarkan filter yang dipilih.'
                        : isPeriode
                            ? 'Pilih periode terlebih dahulu, kemudian tekan tombol Tampilkan Data.'
                            : 'Pilih gudang dan tanggal valuasi, kemudian tekan tombol Tampilkan Data.'}
                </Typography>
            </Box>
        </Box>
    )
}

export default function Index() {
    const token = readToken()

    const { showToast } = useToast()

    /*
    |--------------------------------------------------------------------------
    | Tab
    |--------------------------------------------------------------------------
    */
    const [activeTab, setActiveTab] = useState<number>(0)

    /*
    |--------------------------------------------------------------------------
    | Periode
    |--------------------------------------------------------------------------
    */
    const [listPeriode, setListPeriode] =
        useState<PeriodeOption[]>([])

    const [valPeriode, setValPeriode] =
        useState<PeriodeOption | null>(null)

    const [loadingPeriode, setLoadingPeriode] =
        useState<boolean>(false)

    /*
    |--------------------------------------------------------------------------
    | Gudang
    |--------------------------------------------------------------------------
    */
    const [listGudang, setListGudang] =
        useState<GudangOption[]>([])

    const [valGudang, setValGudang] =
        useState<GudangOption | null>(null)

    const [loadingGudang, setLoadingGudang] =
        useState<boolean>(false)

    /*
    |--------------------------------------------------------------------------
    | Tanggal
    |--------------------------------------------------------------------------
    */
    const defaultDate = moment().format('YYYY-MM-DD')

    const [tgl, setTgl] =
        useState<string>(defaultDate)

    /*
    |--------------------------------------------------------------------------
    | Data
    |--------------------------------------------------------------------------
    */
    const [listData, setListData] =
        useState<ReportRow[]>([])

    const [keyword, setKeyword] =
        useState<string>('')

    const [loadingData, setLoadingData] =
        useState<boolean>(false)

    const [hasSearched, setHasSearched] =
        useState<boolean>(false)

    const [lastUpdated, setLastUpdated] =
        useState<string>('-')

    /*
    |--------------------------------------------------------------------------
    | Initial Load
    |--------------------------------------------------------------------------
    */
    useEffect(() => {
        void fetchPeriode()
        void fetchGudang()
    }, [])

    /*
    |--------------------------------------------------------------------------
    | Fetch Periode
    |--------------------------------------------------------------------------
    */
    const fetchPeriode = async (): Promise<void> => {
        setLoadingPeriode(true)

        try {
            const response = await getPeriode(token)

            const rawPeriode =
                normalizePeriodeResponse(response)

            /*
             * Requirement:
             * hanya Juni 2026 ke atas.
             */
            const minimalDate =
                moment(MIN_PERIODE, 'YYYY-MM-DD')

            const filtered = rawPeriode
                .filter(item => {
                    const periodeDate =
                        getPeriodeDate(item)

                    /*
                     * Jika tidak bisa menentukan tanggal periode,
                     * jangan ditampilkan agar filter Juni ke atas tetap aman.
                     */
                    if (!periodeDate) {
                        return false
                    }

                    return (
                        periodeDate.isSame(
                            minimalDate,
                            'month'
                        ) ||
                        periodeDate.isAfter(
                            minimalDate,
                            'month'
                        )
                    )
                })
                .sort((a, b) => {
                    const dateA = getPeriodeDate(a)
                    const dateB = getPeriodeDate(b)

                    if (!dateA || !dateB) {
                        return 0
                    }

                    /*
                     * Terbaru di atas.
                     */
                    return dateB.valueOf() - dateA.valueOf()
                })

            setListPeriode(filtered)

            /*
             * Periode terbaru dijadikan default.
             */
            if (filtered.length > 0) {
                setValPeriode(current =>
                    current ?? filtered[0]
                )
            }
        } catch (error) {
            showToast(
                getErrorMessage(
                    error,
                    'Gagal mengambil daftar periode.'
                ),
                'error'
            )
        } finally {
            setLoadingPeriode(false)
        }
    }

    /*
    |--------------------------------------------------------------------------
    | Fetch Gudang
    |--------------------------------------------------------------------------
    */
    const fetchGudang = async (): Promise<void> => {
        setLoadingGudang(true)

        try {
            const response = await axios.get(
                `${apiUrl.apiUrl}/report/valuasi-stock-wh/warehouses`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            if (!response.data?.success) {
                showToast(
                    response.data?.message ||
                    'Daftar gudang gagal diambil.',
                    'error'
                )

                return
            }

            const gudang: GudangOption[] =
                Array.isArray(response.data?.data)
                    ? response.data.data.map(
                        (item: any) => ({
                            id: String(item.id),
                            nama: String(item.nama ?? '')
                        })
                    )
                    : []

            setListGudang(gudang)

            /*
             * Gudang pertama sebagai default.
             */
            if (gudang.length > 0) {
                setValGudang(current =>
                    current ?? gudang[0]
                )
            }
        } catch (error) {
            showToast(
                getErrorMessage(
                    error,
                    'Gagal mengambil daftar gudang.'
                ),
                'error'
            )
        } finally {
            setLoadingGudang(false)
        }
    }

    /*
    |--------------------------------------------------------------------------
    | Valuasi Barang
    |--------------------------------------------------------------------------
    */
    const getValuasiBarang =
        async (): Promise<void> => {
            if (!valPeriode?.id) {
                showToast(
                    'Periode harus dipilih terlebih dahulu.',
                    'error'
                )

                return
            }

            setLoadingData(true)
            setKeyword('')

            try {
                const response = await axios.get(
                    `${apiUrl.apiUrl}/accounting/valuasi-barang/list`,
                    {
                        params: {
                            id_periode: String(
                                valPeriode.id
                            )
                        },

                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                )

                if (!response.data?.success) {
                    showToast(
                        response.data?.message ||
                        'Data valuasi barang gagal diambil.',
                        'error'
                    )

                    return
                }

                const rows =
                    Array.isArray(response.data?.data)
                        ? response.data.data.map(
                            (
                                row: ReportRow,
                                index: number
                            ) => ({
                                ...row,

                                /*
                                 * ID internal DataGrid.
                                 */
                                id: index + 1
                            })
                        )
                        : []

                setListData(rows)
                setHasSearched(true)

                setLastUpdated(
                    new Date().toLocaleString(
                        'id-ID'
                    )
                )

                showToast(
                    response.data?.message ||
                    'Data valuasi barang berhasil diambil.',
                    'success'
                )
            } catch (error) {
                setListData([])
                setHasSearched(true)

                showToast(
                    getErrorMessage(
                        error,
                        'Gagal mengambil data valuasi barang.'
                    ),
                    'error'
                )
            } finally {
                setLoadingData(false)
            }
        }

    /*
    |--------------------------------------------------------------------------
    | Valuasi Barang Gudang
    |--------------------------------------------------------------------------
    */
    const getValuasiBarangGudang =
        async (): Promise<void> => {
            if (!tgl) {
                showToast(
                    'Tanggal valuasi harus diisi.',
                    'error'
                )

                return
            }

            if (!valGudang?.id) {
                showToast(
                    'Gudang harus dipilih.',
                    'error'
                )

                return
            }

            setLoadingData(true)
            setKeyword('')

            try {
                const response = await axios.get(
                    `${apiUrl.apiUrl}/accounting/valuasi-barang/gudang/list`,
                    {
                        params: {
                            tgl,
                            mmgudang_id: valGudang.id
                        },

                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                )

                if (!response.data?.success) {
                    showToast(
                        response.data?.message ||
                        'Data valuasi barang per gudang gagal diambil.',
                        'error'
                    )

                    return
                }

                const rows =
                    Array.isArray(response.data?.data)
                        ? response.data.data.map(
                            (
                                row: ReportRow,
                                index: number
                            ) => ({
                                ...row,
                                id: index + 1
                            })
                        )
                        : []

                setListData(rows)
                setHasSearched(true)

                setLastUpdated(
                    new Date().toLocaleString(
                        'id-ID'
                    )
                )

                showToast(
                    response.data?.message ||
                    'Data valuasi barang per gudang berhasil diambil.',
                    'success'
                )
            } catch (error) {
                setListData([])
                setHasSearched(true)

                showToast(
                    getErrorMessage(
                        error,
                        'Gagal mengambil data valuasi barang per gudang.'
                    ),
                    'error'
                )
            } finally {
                setLoadingData(false)
            }
        }

    /*
    |--------------------------------------------------------------------------
    | Change Tab
    |--------------------------------------------------------------------------
    */
    const handleChangeTab = (
        _: SyntheticEvent,
        newValue: number
    ): void => {
        setActiveTab(newValue)

        /*
         * Data hasil tab sebelumnya tidak dibawa
         * ke tab yang baru.
         */
        setListData([])
        setKeyword('')
        setHasSearched(false)
        setLastUpdated('-')
    }

    /*
    |--------------------------------------------------------------------------
    | Reset
    |--------------------------------------------------------------------------
    */
    const resetFilter = (): void => {
        setListData([])
        setKeyword('')
        setHasSearched(false)
        setLastUpdated('-')

        if (activeTab === 0) {
            setValPeriode(
                listPeriode[0] ?? null
            )
        } else {
            setTgl(defaultDate)

            setValGudang(
                listGudang[0] ?? null
            )
        }
    }

    /*
    |--------------------------------------------------------------------------
    | Search Client Side
    |--------------------------------------------------------------------------
    */
    const filteredData =
        useMemo(() => {
            const search =
                keyword
                    .trim()
                    .toLowerCase()

            if (!search) {
                return listData
            }

            return listData.filter(row =>
                Object.values(row).some(
                    value =>
                        String(value ?? '')
                            .toLowerCase()
                            .includes(search)
                )
            )
        }, [
            keyword,
            listData
        ])

    /*
    |--------------------------------------------------------------------------
    | Label
    |--------------------------------------------------------------------------
    */
    const formattedDate = tgl
        ? moment(
            tgl,
            'YYYY-MM-DD'
        ).format('DD MMMM YYYY')
        : '-'

    const isTabPeriode =
        activeTab === 0

    const canSearch =
        isTabPeriode
            ? Boolean(valPeriode?.id)
            : Boolean(
                tgl &&
                valGudang?.id
            )

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Card
                    sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: theme =>
                            `1px solid ${theme.palette.divider}`,
                        boxShadow:
                            '0 18px 50px rgba(15, 23, 42, 0.08)'
                    }}
                >
                    {/* =====================================================
              HEADER
          ====================================================== */}
                    <Box
                        sx={{
                            px: {
                                xs: 2.5,
                                md: 4
                            },

                            py: {
                                xs: 3,
                                md: 3.75
                            },

                            color: 'common.white',

                            background:
                                'linear-gradient(135deg, #111827 0%, #1e3a8a 52%, #2563eb 100%)'
                        }}
                    >
                        <Stack
                            direction={{
                                xs: 'column',
                                sm: 'row'
                            }}
                            spacing={2.25}
                            alignItems={{
                                xs: 'flex-start',
                                sm: 'center'
                            }}
                            justifyContent='space-between'
                        >
                            <Stack
                                direction='row'
                                spacing={2}
                                alignItems='center'
                            >
                                <Box
                                    sx={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: 3,
                                        display: 'grid',
                                        placeItems: 'center',
                                        backgroundColor:
                                            'rgba(255,255,255,0.14)',
                                        border:
                                            '1px solid rgba(255,255,255,0.2)'
                                    }}
                                >
                                    <Icon
                                        icon='mdi:chart-box-outline'
                                        fontSize={30}
                                    />
                                </Box>

                                <Box>
                                    <Typography
                                        variant='h4'
                                        sx={{
                                            fontWeight: 800
                                        }}
                                    >
                                        Valuasi Barang
                                    </Typography>

                                    <Typography
                                        variant='body2'
                                        sx={{
                                            mt: 0.5,
                                            color:
                                                'rgba(255,255,255,0.78)'
                                        }}
                                    >
                                        Laporan nilai persediaan
                                        berdasarkan periode dan
                                        lokasi gudang.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Chip
                                icon={
                                    <Icon
                                        icon='mdi:calculator-variant-outline'
                                        fontSize={18}
                                    />
                                }
                                label='Accounting'
                                sx={{
                                    color: 'common.white',
                                    backgroundColor:
                                        'rgba(255,255,255,0.14)',
                                    border:
                                        '1px solid rgba(255,255,255,0.2)',

                                    '& .MuiChip-icon': {
                                        color: 'inherit'
                                    }
                                }}
                            />
                        </Stack>
                    </Box>

                    {loadingData && (
                        <LinearProgress />
                    )}

                    {/* =====================================================
              TAB
          ====================================================== */}
                    <Box
                        sx={{
                            px: {
                                xs: 2,
                                md: 3.5
                            },

                            pt: 2,

                            borderBottom:
                                '1px solid',

                            borderColor:
                                'divider'
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={handleChangeTab}
                            variant='scrollable'
                            scrollButtons='auto'
                        >
                            <Tab
                                icon={
                                    <Icon
                                        icon='mdi:chart-box-outline'
                                        fontSize={20}
                                    />
                                }
                                iconPosition='start'
                                label='Valuasi Barang'
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    minHeight: 54
                                }}
                            />

                            <Tab
                                icon={
                                    <Icon
                                        icon='mdi:warehouse'
                                        fontSize={20}
                                    />
                                }
                                iconPosition='start'
                                label='Valuasi Barang per Gudang'
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    minHeight: 54
                                }}
                            />
                        </Tabs>
                    </Box>

                    <CardContent
                        sx={{
                            p: {
                                xs: 2,
                                md: 3.5
                            }
                        }}
                    >
                        {/* =====================================================
                FILTER
            ====================================================== */}
                        <Paper
                            variant='outlined'
                            sx={{
                                p: {
                                    xs: 2,
                                    md: 2.75
                                },

                                mb: 3,

                                borderRadius: 3,

                                backgroundColor:
                                    'background.default'
                            }}
                        >
                            <Grid
                                container
                                spacing={2}
                                alignItems='center'
                            >
                                <Grid
                                    item
                                    xs={12}
                                    md={2.5}
                                >
                                    <Typography
                                        variant='subtitle1'
                                        sx={{
                                            fontWeight: 700
                                        }}
                                    >
                                        Filter Laporan
                                    </Typography>

                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                    >
                                        {isTabPeriode
                                            ? 'Pilih periode valuasi barang.'
                                            : 'Pilih tanggal dan gudang.'}
                                    </Typography>
                                </Grid>

                                {/* ================================================
                    TAB PERIODE
                ================================================= */}
                                {isTabPeriode && (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={5}
                                    >
                                        <Autocomplete<PeriodeOption>
                                            fullWidth
                                            value={valPeriode}
                                            options={listPeriode}
                                            loading={loadingPeriode}
                                            disabled={loadingData}
                                            isOptionEqualToValue={(
                                                option,
                                                value
                                            ) =>
                                                String(option.id) ===
                                                String(value?.id)
                                            }
                                            getOptionLabel={option =>
                                                String(
                                                    option?.periode ??
                                                    option?.kode_periode ??
                                                    ''
                                                )
                                            }
                                            onChange={(
                                                _,
                                                newValue
                                            ) => {
                                                setValPeriode(
                                                    newValue
                                                )

                                                setListData([])
                                                setHasSearched(false)
                                                setKeyword('')
                                                setLastUpdated('-')
                                            }}
                                            renderOption={(
                                                props,
                                                option
                                            ) => (
                                                <li
                                                    {...props}
                                                    key={String(
                                                        option.id
                                                    )}
                                                >
                                                    <Box>
                                                        <Typography
                                                            variant='body2'
                                                            sx={{
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {String(
                                                                option.periode ??
                                                                option.kode_periode ??
                                                                ''
                                                            )}
                                                        </Typography>

                                                        <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                        >
                                                            ID:{' '}
                                                            {String(
                                                                option.id
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </li>
                                            )}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label='Periode'
                                                    placeholder='Pilih periode'
                                                    InputProps={{
                                                        ...params.InputProps,

                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position='start'>
                                                                    <Icon icon='mdi:calendar-month-outline' />
                                                                </InputAdornment>

                                                                {
                                                                    params
                                                                        .InputProps
                                                                        .startAdornment
                                                                }
                                                            </>
                                                        ),

                                                        endAdornment: (
                                                            <>
                                                                {loadingPeriode && (
                                                                    <CircularProgress
                                                                        color='inherit'
                                                                        size={20}
                                                                    />
                                                                )}

                                                                {
                                                                    params
                                                                        .InputProps
                                                                        .endAdornment
                                                                }
                                                            </>
                                                        )
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}

                                {/* ================================================
                    TAB GUDANG
                ================================================= */}
                                {!isTabPeriode && (
                                    <>
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={2.5}
                                        >
                                            <TextField
                                                fullWidth
                                                type='date'
                                                label='Tanggal Valuasi'
                                                value={tgl}
                                                disabled={loadingData}
                                                onChange={event => {
                                                    setTgl(
                                                        event.target.value
                                                    )

                                                    setListData([])
                                                    setHasSearched(false)
                                                    setKeyword('')
                                                    setLastUpdated('-')
                                                }}
                                                InputLabelProps={{
                                                    shrink: true
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position='start'>
                                                            <Icon icon='mdi:calendar-outline' />
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={3}
                                        >
                                            <Autocomplete<GudangOption>
                                                fullWidth
                                                value={valGudang}
                                                options={listGudang}
                                                loading={loadingGudang}
                                                disabled={loadingData}
                                                isOptionEqualToValue={(
                                                    option,
                                                    value
                                                ) =>
                                                    option.id ===
                                                    value?.id
                                                }
                                                getOptionLabel={option =>
                                                    option?.nama || ''
                                                }
                                                onChange={(
                                                    _,
                                                    newValue
                                                ) => {
                                                    setValGudang(
                                                        newValue
                                                    )

                                                    setListData([])
                                                    setHasSearched(false)
                                                    setKeyword('')
                                                    setLastUpdated('-')
                                                }}
                                                renderOption={(
                                                    props,
                                                    option
                                                ) => (
                                                    <li
                                                        {...props}
                                                        key={option.id}
                                                    >
                                                        <Box>
                                                            <Typography
                                                                variant='body2'
                                                                sx={{
                                                                    fontWeight:
                                                                        600
                                                                }}
                                                            >
                                                                {
                                                                    option.nama
                                                                }
                                                            </Typography>

                                                            <Typography
                                                                variant='caption'
                                                                color='text.secondary'
                                                            >
                                                                ID:{' '}
                                                                {
                                                                    option.id
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </li>
                                                )}
                                                renderInput={params => (
                                                    <TextField
                                                        {...params}
                                                        label='Gudang'
                                                        placeholder='Pilih gudang'
                                                        InputProps={{
                                                            ...params.InputProps,

                                                            startAdornment: (
                                                                <>
                                                                    <InputAdornment position='start'>
                                                                        <Icon icon='mdi:warehouse' />
                                                                    </InputAdornment>

                                                                    {
                                                                        params
                                                                            .InputProps
                                                                            .startAdornment
                                                                    }
                                                                </>
                                                            ),

                                                            endAdornment: (
                                                                <>
                                                                    {loadingGudang && (
                                                                        <CircularProgress
                                                                            color='inherit'
                                                                            size={20}
                                                                        />
                                                                    )}

                                                                    {
                                                                        params
                                                                            .InputProps
                                                                            .endAdornment
                                                                    }
                                                                </>
                                                            )
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {/* ================================================
                    ACTION
                ================================================= */}
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={
                                        isTabPeriode
                                            ? 2.5
                                            : 2
                                    }
                                >
                                    <Button
                                        fullWidth
                                        variant='contained'
                                        size='large'
                                        disabled={
                                            !canSearch ||
                                            loadingData
                                        }
                                        startIcon={
                                            loadingData ? (
                                                <CircularProgress
                                                    size={18}
                                                    color='inherit'
                                                />
                                            ) : (
                                                <Icon icon='mdi:magnify' />
                                            )
                                        }
                                        onClick={() => {
                                            if (
                                                isTabPeriode
                                            ) {
                                                void getValuasiBarang()
                                            } else {
                                                void getValuasiBarangGudang()
                                            }
                                        }}
                                        sx={{
                                            minHeight: 52,
                                            borderRadius: 2.25,
                                            textTransform:
                                                'none',
                                            fontWeight: 700
                                        }}
                                    >
                                        {loadingData
                                            ? 'Memuat...'
                                            : 'Tampilkan Data'}
                                    </Button>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    md={2}
                                >
                                    <Button
                                        fullWidth
                                        variant='outlined'
                                        size='large'
                                        disabled={
                                            loadingData
                                        }
                                        startIcon={
                                            <Icon icon='mdi:refresh' />
                                        }
                                        onClick={
                                            resetFilter
                                        }
                                        sx={{
                                            minHeight: 52,
                                            borderRadius: 2.25,
                                            textTransform:
                                                'none',
                                            fontWeight: 700
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* =====================================================
                SUMMARY
            ====================================================== */}
                        {hasSearched && (
                            <Grid
                                container
                                spacing={2}
                                sx={{
                                    mb: 3
                                }}
                            >
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                >
                                    <MetricCard
                                        icon='mdi:table-large'
                                        label='Jumlah Data'
                                        value={
                                            listData.length.toLocaleString(
                                                'id-ID'
                                            )
                                        }
                                        helper='Baris hasil laporan'
                                    />
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                >
                                    {isTabPeriode ? (
                                        <MetricCard
                                            icon='mdi:calendar-check-outline'
                                            label='Periode'
                                            value={
                                                valPeriode?.periode ||
                                                String(
                                                    valPeriode?.kode_periode ??
                                                    '-'
                                                )
                                            }
                                            helper={`ID: ${valPeriode?.id ??
                                                '-'
                                                }`}
                                        />
                                    ) : (
                                        <MetricCard
                                            icon='mdi:warehouse'
                                            label='Gudang'
                                            value={
                                                valGudang?.nama ||
                                                '-'
                                            }
                                            helper={`ID: ${valGudang?.id ??
                                                '-'
                                                }`}
                                        />
                                    )}
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                >
                                    <MetricCard
                                        icon={
                                            isTabPeriode
                                                ? 'mdi:clock-check-outline'
                                                : 'mdi:calendar-check-outline'
                                        }
                                        label={
                                            isTabPeriode
                                                ? 'Terakhir Diperbarui'
                                                : 'Tanggal Valuasi'
                                        }
                                        value={
                                            isTabPeriode
                                                ? lastUpdated
                                                : formattedDate
                                        }
                                        helper={
                                            isTabPeriode
                                                ? 'Waktu pengambilan data'
                                                : `Diambil: ${lastUpdated}`
                                        }
                                    />
                                </Grid>
                            </Grid>
                        )}

                        {/* =====================================================
                TABLE
            ====================================================== */}
                        <Paper
                            variant='outlined'
                            sx={{
                                overflow: 'hidden',
                                borderRadius: 3,
                                borderColor: 'divider'
                            }}
                        >
                            <Box
                                sx={{
                                    px: {
                                        xs: 2,
                                        md: 2.5
                                    },

                                    py: 2,

                                    display: 'flex',

                                    flexDirection: {
                                        xs: 'column',
                                        sm: 'row'
                                    },

                                    alignItems: {
                                        xs: 'stretch',
                                        sm: 'center'
                                    },

                                    justifyContent:
                                        'space-between',

                                    gap: 1.5
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant='h6'
                                        sx={{
                                            fontWeight: 700
                                        }}
                                    >
                                        {isTabPeriode
                                            ? 'Hasil Valuasi Barang'
                                            : 'Hasil Valuasi Barang per Gudang'}
                                    </Typography>

                                    <Typography
                                        variant='caption'
                                        color='text.secondary'
                                    >
                                        Kolom tabel mengikuti
                                        hasil stored procedure.
                                    </Typography>
                                </Box>

                                {listData.length > 0 && (
                                    <Stack
                                        direction='row'
                                        spacing={1.25}
                                        alignItems='center'
                                    >
                                        <TextField
                                            size='small'
                                            value={keyword}
                                            onChange={event =>
                                                setKeyword(
                                                    event.target.value
                                                )
                                            }
                                            placeholder='Cari dalam hasil...'
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position='start'>
                                                        <Icon icon='mdi:magnify' />
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                minWidth: {
                                                    sm: 260
                                                }
                                            }}
                                        />

                                        <Chip
                                            size='small'
                                            label={`${filteredData.length.toLocaleString(
                                                'id-ID'
                                            )} baris`}
                                            variant='outlined'
                                        />
                                    </Stack>
                                )}
                            </Box>

                            <Divider />

                            {loadingData ? (
                                <Box
                                    sx={{
                                        minHeight: 300,
                                        display: 'grid',
                                        placeItems: 'center'
                                    }}
                                >
                                    <Stack
                                        alignItems='center'
                                        spacing={1.5}
                                    >
                                        <CircularProgress
                                            size={36}
                                        />

                                        <Typography
                                            variant='body2'
                                            color='text.secondary'
                                        >
                                            {isTabPeriode
                                                ? 'Mengambil data valuasi barang...'
                                                : 'Mengambil data valuasi barang per gudang...'}
                                        </Typography>
                                    </Stack>
                                </Box>
                            ) : !hasSearched ||
                                listData.length === 0 ? (
                                <EmptyState
                                    type={
                                        isTabPeriode
                                            ? 'periode'
                                            : 'gudang'
                                    }
                                    hasSearched={
                                        hasSearched
                                    }
                                />
                            ) : (
                                <Box
                                    sx={{
                                        p: {
                                            xs: 1,
                                            md: 2
                                        },
                                        overflowX: 'auto'
                                    }}
                                >
                                    <MsiDatagrid
                                        array={
                                            filteredData
                                        }
                                        actions={
                                            noActions
                                        }
                                        hiddenColumns={[
                                            'id'
                                        ]}
                                    />
                                </Box>
                            )}
                        </Paper>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    )
}